import React, { useMemo } from 'react';
import { Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

// Amber used for PENDING leave, matching the CalendarView legend's
// "pendingApproval" warning colour and the backend PENDING_COLOR.
const PENDING_COLOR = '#FFC107';
const DAYS_IN_WEEK = 7;

// Parse a 'YYYY-MM-DD' string as UTC midnight, consistent with calendarUtils.js,
// so day-by-day comparison never shifts under a non-UTC local timezone.
const parseUtcMidnight = (isoDate) => new Date(isoDate + 'T00:00:00Z');

// Weekday header order is Monday → Sunday (firstDay=1, matching FullCalendar).
// JS getUTCDay() is 0=Sun..6=Sat, so weekend = 0 or 6.
const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const MiniMonthGrid = ({ year, month, events = [] }) => {
  const { t } = useTranslation();

  const { weeks } = useMemo(() => {
    const firstOfMonth = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    // Build the per-day coverage list using PLAIN INCLUSIVE bounds:
    // event.start <= day <= event.end (no +1 — this is NOT FullCalendar's
    // exclusive-end model; adding +1 here would re-introduce the off-by-one the
    // date-inclusivity contract forbids).
    const days = [];
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum += 1) {
      const dayDate = new Date(Date.UTC(year, month, dayNum));
      const dow = dayDate.getUTCDay();
      const isWeekend = dow === 0 || dow === 6;
      // Weekends are never part of a leave bar — mirrors the single-month view,
      // which excludes Sat/Sun via splitIntoWeekdaySegments. (We deliberately do NOT
      // use that util here: it returns FullCalendar exclusive-end (+1) segments, which
      // would break this component's inclusive start<=day<=end membership test.)
      const covering = isWeekend
        ? []
        : events.filter((event) => {
            const start = parseUtcMidnight(event.start);
            const end = parseUtcMidnight(event.end);
            return start <= dayDate && dayDate <= end;
          });
      days.push({
        dayNum,
        isWeekend,
        covering: covering.map((event) => ({
          employeeName: event.employeeName,
          status: event.status,
          leaveType: event.leaveType,
          color: event.color,
        })),
      });
    }

    // Leading blanks so day 1 lands under its weekday column (Mon-first grid).
    const firstDow = firstOfMonth.getUTCDay();
    const leadingBlanks = (firstDow + 6) % DAYS_IN_WEEK; // Mon=0 .. Sun=6
    const cells = [...Array(leadingBlanks).fill(null), ...days];
    while (cells.length % DAYS_IN_WEEK !== 0) {
      cells.push(null);
    }
    const weekRows = [];
    for (let i = 0; i < cells.length; i += DAYS_IN_WEEK) {
      weekRows.push(cells.slice(i, i + DAYS_IN_WEEK));
    }

    return { weeks: weekRows };
  }, [year, month, events]);

  const monthLabel = `${t(`calendar.months.m${month}`)} ${year}`;

  const renderDay = (cell, index) => {
    if (cell === null) {
      return <div key={`blank-${index}`} className="mini-month-day mini-month-day--blank" />;
    }

    const isCovered = cell.covering.length > 0;
    const hasApproved = cell.covering.some((c) => c.status === 'APPROVED');
    // Approved day takes the department colour (event.color from the backend);
    // a day with only pending requests is amber.
    const approvedColor = cell.covering.find((c) => c.status === 'APPROVED')?.color;
    const backgroundColor = !isCovered
      ? undefined
      : hasApproved
        ? (approvedColor || PENDING_COLOR)
        : PENDING_COLOR;

    const dayClassNames = ['mini-month-day'];
    if (cell.isWeekend) dayClassNames.push('mini-month-day--weekend');
    if (isCovered) dayClassNames.push('mini-month-day--covered');

    const dayCell = (
      <div
        className={dayClassNames.join(' ')}
        style={isCovered ? { backgroundColor, color: '#fff' } : undefined}
        data-testid={isCovered ? 'mini-month-day-covered' : 'mini-month-day'}
      >
        {cell.dayNum}
      </div>
    );

    if (!isCovered) {
      return <React.Fragment key={`day-${cell.dayNum}`}>{dayCell}</React.Fragment>;
    }

    const tooltipContent = (
      <div>
        {cell.covering.map((c, i) => (
          <div key={`${c.employeeName}-${i}`}>
            {`${c.employeeName} — ${t(`leave.form.types.${c.leaveType}`)} (${t(`leave.status.${c.status}`)})`}
          </div>
        ))}
      </div>
    );

    return (
      <Tooltip key={`day-${cell.dayNum}`} title={tooltipContent}>
        {dayCell}
      </Tooltip>
    );
  };

  return (
    <div className="mini-month-grid" data-testid="mini-month-grid">
      <Text strong className="mini-month-title">{monthLabel}</Text>
      <div className="mini-month-weekdays">
        {WEEKDAY_KEYS.map((key) => (
          <div key={key} className="mini-month-weekday">
            {t(`calendar.weekdaysShort.${key}`)}
          </div>
        ))}
      </div>
      <div className="mini-month-days">
        {weeks.flat().map((cell, index) => renderDay(cell, index))}
      </div>
    </div>
  );
};

export default MiniMonthGrid;
